const FOCUSABLE_SELECTOR =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export const clampIndex = (index: number, count: number): number => Math.max(0, Math.min(count - 1, index));

export const getPanelsFromTrack = (track: HTMLDivElement | null): HTMLElement[] => {
    if (!track) {
        return [];
    }

    const directChildren = Array.from(track.children).filter(
        (child): child is HTMLElement => child instanceof HTMLElement,
    );

    const explicitPanels = directChildren.filter((child) => child.hasAttribute('data-carousel-panel'));

    if (explicitPanels.length) {
        return explicitPanels;
    }

    const list = track.querySelector<HTMLElement>(':scope > ul, :scope > ol');
    if (list) {
        const listItems = Array.from(list.children).filter(
            (child): child is HTMLElement => child instanceof HTMLElement && child.tagName === 'LI',
        );
        if (listItems.length) {
            return listItems;
        }
    }

    return directChildren;
};

export const getDotsFromContainer = (dotsContainer: HTMLDivElement | null): HTMLElement[] =>
    dotsContainer ? (Array.from(dotsContainer.children) as HTMLElement[]) : [];

export const getPanelIndexFromTarget = (target: EventTarget | null, panels: HTMLElement[]): number => {
    if (!(target instanceof HTMLElement)) {
        return -1;
    }

    const panel = target.closest<HTMLElement>('[data-carousel-panel]');
    if (panel) {
        return panels.indexOf(panel);
    }

    return panels.findIndex((candidate) => candidate === target || candidate.contains(target));
};

export const scrollTrackToPanel = (track: HTMLDivElement | null, panel: HTMLElement): void => {
    if (!track) {
        return;
    }

    const panelRect = panel.getBoundingClientRect();
    const trackRect = track.getBoundingClientRect();
    const centeredOffset = (track.clientWidth - panelRect.width) / 2;
    const targetX = panelRect.left - trackRect.left + track.scrollLeft - centeredOffset;
    const maxScrollLeft = track.scrollWidth - track.clientWidth;
    const clampedTargetX = Math.max(0, Math.min(targetX, maxScrollLeft));

    track.scrollTo({ left: clampedTargetX, behavior: 'auto' });
};

export const focusFirstInteractiveInPanel = (panel: HTMLElement): HTMLElement | null => {
    const focusable = panel.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);

    if (focusable) {
        focusable.focus({ preventScroll: true });
        return focusable;
    }

    // Keep focus within the active panel even when it has no interactive children.
    if (panel.tabIndex < 0) {
        panel.setAttribute('tabindex', '-1');
    }
    panel.focus({ preventScroll: true });
    return panel;
};
